import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { PageHeader } from '@/components/layout/PageHeader'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { KpiCard } from '@/components/layout/KpiCard'
import { Button } from '@/components/ui/Button'
import { EmptyState } from '@/components/ui/EmptyState'
import { StockStatusBadge } from '@/features/proto/ProtoBadges'
import { formatDate, formatDateFull } from '@/lib/dateFormat'
import {
  getMaterialStock,
  recordStatusTransfer,
  type MaterialStockDetailDto,
} from '@/services/inventory'

function formatQtyWithUom(qty: number, uom?: string | null): string {
  return `${qty.toLocaleString('vi-VN')} ${uom || ''}`.trim()
}

export function StockDetailPage() {
  const { materialId = '' } = useParams()
  const navigate = useNavigate()
  const [stock, setStock] = useState<MaterialStockDetailDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)
  const [actionMessage, setActionMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function loadData() {
    setLoading(true)
    setError(null)
    try {
      const data = await getMaterialStock(materialId)
      setStock(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Lỗi tải tồn kho')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadData()
  }, [materialId])

  async function handleQcPass() {
    if (!stock || stock.quarantine <= 0) return
    setActionLoading(true)
    setError(null)
    setActionMessage(null)
    try {
      await recordStatusTransfer({
        material_id: stock.material_id,
        from_status: 'QUARANTINE',
        to_status: 'AVAILABLE',
        qty_base: stock.quarantine,
        reason: 'QC Pass',
      })
      setActionMessage('Đã chuyển toàn bộ hàng từ Quarantine sang Available.')
      await loadData()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Thao tác QC Pass thất bại')
    } finally {
      setActionLoading(false)
    }
  }

  if (loading) {
    return <div className="py-12 text-center text-sm text-slate-400">Đang tải tồn kho...</div>
  }

  if (!stock) {
    return (
      <EmptyState
        title="Không tìm thấy vật tư"
        action={
          <Button variant="secondary" onClick={() => navigate('/warehouse/stock')}>
            Về tồn kho
          </Button>
        }
      />
    )
  }

  const m = stock
  const uom = m.uom_default || '—'
  const physical = m.available + m.quarantine + m.blocked + m.damaged + m.rework
  const projected = m.available + m.incoming_base

  return (
    <div>
      <Breadcrumb
        items={[
          { label: 'Trang chủ', to: '/' },
          { label: 'Kho', to: '/warehouse' },
          { label: 'Tồn kho', to: '/warehouse/stock' },
          { label: m.name },
        ]}
      />
      <PageHeader
        title={m.name}
        subtitle={`Loại ${m.kind} · ĐVT gốc ${uom} · Trạng thái ${m.status}`}
        actions={
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => navigate(`/purchasing/materials/${m.material_id}`)}
            >
              Cấu hình UOM Factor
            </Button>
            <Button onClick={() => navigate('/purchasing/suggestions')}>
              Đề xuất mua
            </Button>
          </div>
        }
      />

      {error ? (
        <div className="mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">
          {error}
        </div>
      ) : null}

      {actionMessage ? (
        <div className="mb-3 rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
          {actionMessage}
        </div>
      ) : null}

      {m.incoming_unresolved ? (
        <div className="mb-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <b>Chú ý:</b> Đang có đơn mua hàng (PO) cho vật tư này chưa quy đổi được đơn vị về đơn vị gốc (thiếu UOM factor). Số lượng đang về có thể chưa phản ánh đầy đủ. Hãy bấm &quot;Cấu hình UOM Factor&quot; để thiết lập hệ số.
        </div>
      ) : null}

      <div className="mb-4 grid grid-cols-2 gap-2 xl:grid-cols-4">
        <KpiCard label="Tồn vật lý" value={formatQtyWithUom(physical, uom)} />
        <KpiCard label="Có thể dùng (Available)" value={formatQtyWithUom(m.available, uom)} />
        <KpiCard
          label="Đang về (Incoming)"
          value={formatQtyWithUom(m.incoming_base, uom)}
          hint={m.incoming_unresolved ? 'Chưa quy đổi hết UOM' : undefined}
          tone={m.incoming_unresolved ? 'warning' : 'default'}
        />
        <KpiCard label="Dự kiến (Projected)" value={formatQtyWithUom(projected, uom)} />
      </div>

      <div className="mb-4 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] p-4 shadow-[var(--shadow-level-1)]">
        <h2 className="mb-2 text-sm font-semibold">Trạng thái tồn kho (Buckets)</h2>
        <div className="flex flex-wrap gap-3 text-sm">
          {(
            [
              ['AVAILABLE', m.available],
              ['QUARANTINE', m.quarantine],
              ['BLOCKED', m.blocked],
              ['DAMAGED', m.damaged],
              ['REWORK', m.rework],
            ] as const
          ).map(([st, qty]) => (
            <div
              key={st}
              className="flex items-center gap-2 rounded border border-[var(--color-border)] px-2 py-1"
            >
              <StockStatusBadge status={st} />
              <span className="tabular-nums font-medium">
                {formatQtyWithUom(qty, uom)}
              </span>
            </div>
          ))}
        </div>
        <p className="mt-2 text-xs text-[var(--color-text-muted)]">
          QUARANTINE / BLOCKED / DAMAGED / REWORK không tính vào Available.
        </p>

        {m.quarantine > 0 ? (
          <Button
            className="mt-3"
            size="sm"
            variant="secondary"
            onClick={() => void handleQcPass()}
            disabled={actionLoading}
          >
            {actionLoading ? 'Đang xử lý...' : `QC Pass: Quarantine → Available (${formatQtyWithUom(m.quarantine, uom)})`}
          </Button>
        ) : null}
      </div>

      {m.movements && m.movements.length > 0 ? (
        <div className="mb-4 overflow-hidden rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] shadow-[var(--shadow-level-1)]">
          <div className="border-b border-[var(--color-border)] px-3 py-2 text-sm font-semibold">
            Biến động gần đây
          </div>
          <ul className="divide-y divide-[var(--color-border)] text-sm">
            {m.movements.slice(0, 10).map((t) => (
              <li
                key={t.id}
                className="flex flex-wrap items-center justify-between gap-2 px-3 py-2"
              >
                <span className="text-[var(--color-text-muted)]">
                  <span title={t.at ? formatDateFull(t.at) : undefined}>
                    {t.at ? formatDate(t.at) : '—'}
                  </span>{' '}
                  · {t.type} · {t.doc_code} · {t.stock_status}
                </span>
                <span
                  className={`tabular-nums font-medium ${
                    t.qty_base < 0 ? 'text-red-700' : 'text-emerald-700'
                  }`}
                >
                  {formatQtyWithUom(t.qty_base, t.base_uom || uom)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <Link to="/warehouse/transactions" className="text-sm text-[var(--color-link)]">
        Xem toàn bộ lịch sử biến động kho →
      </Link>
    </div>
  )
}
